/**
 * MultiSelectDropdown Component
 *
 * A reusable dropdown component for selecting multiple items.
 */

import { useState, useRef, useEffect, useMemo } from 'react';
import { ArrowIcon, PlusIcon } from '../../../icons';
import styles from './SelectDropdown.module.scss';
import { ZprLabel } from '@zpr-stencil';
import { EditLabel } from '../EditLabel/EditLabel';


export interface MultiSelectDropdownOption {
  value: string;
  label: string;
  selected: boolean;
  color?: string; // Optional color for label styling (e.g., for status labels)
}

export interface MultiSelectDropdownAction {
  icon: React.ReactNode;
  label: string;
  /** When true, clicking opens the EditLabel panel. onClick receives (value, newLabel, newColor). */
  isEditLabel?: boolean;
  /** When true, clicking shows an inline confirm prompt before calling onClick. */
  isConfirmDelete?: boolean;
  onClick: (value: string, newLabel?: string, newColor?: string) => void;
}

export interface MultiSelectDropdownProps {
  /** Placeholder text */
  placeholder?: string;
  /** Available options */
  options: MultiSelectDropdownOption[];
  /** Callback when selection changes */
  onChange?: (selectedValues: string[]) => void;
  /** Dropdown header title */
  title?: string;
  /** Enable search functionality (default: false) */
  enableSearch?: boolean;
  /** If true, the trigger button only shows the label without the count (default: false) */
  showLabelOnly?: boolean;
  /** If true, options are styled as ZprLabel */
  isLabelOptions?: boolean;
  /** Custom CSS class */
  className?: string;
  /** Direction the menu opens relative to the trigger (default: 'down') */
  direction?: 'up' | 'down' | 'left' | 'right';
  /** Optional create action callback — renders a Create button at the bottom of the menu */
  onCreate?: (label: string) => void;
  /** Optional action buttons rendered next to each option */
  actions?: MultiSelectDropdownAction[];
}

export const MultiSelectDropdown = ({
  placeholder = 'Select items...',
  options,
  onChange,
  title,
  enableSearch = false,
  showLabelOnly = false,
  isLabelOptions = false,
  className = '',
  direction = 'down',
  onCreate,
  actions,
}: MultiSelectDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedValues, setSelectedValues] = useState<string[]>(() =>
    options.filter(opt => opt.selected).map(opt => opt.value)
  );
  const [editingValue, setEditingValue] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState('');
  const [confirmDeleteValue, setConfirmDeleteValue] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const [menuMaxHeight, setMenuMaxHeight] = useState<number>(300);

  // Sync internal state when options prop changes
  useEffect(() => {
    setSelectedValues(options.filter(opt => opt.selected).map(opt => opt.value));
  }, [options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !event.composedPath().includes(dropdownRef.current)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on scroll so menu doesn't detach from trigger
  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = (e: Event) => {
      if (dropdownRef.current && e.composedPath().includes(dropdownRef.current)) return;
      setIsOpen(false);
      setSearchQuery('');
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  const handleToggleOption = (value: string) => {
    if (!onChange) return;
    const newSelectedValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    setSelectedValues(newSelectedValues);
    onChange(newSelectedValues);
  };

  const handleClearAll = () => {
    if (!onChange) return;
    setSelectedValues([]);
    onChange([]);
  };

  const getSelectedLabels = () => {
    return options
      .filter(opt => selectedValues.includes(opt.value))
      .map(opt => opt.label)
      .join(', ');
  };

  const filteredOptions = useMemo(() => {
    return (enableSearch
    ? options.filter(option =>
        String(option.label).toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options);
  }, [options, searchQuery, enableSearch]);

  const canCreateNewLabel = useMemo(() => {
    if (!onCreate || !searchQuery) return false;
    const exists = options.some(option => String(option.label).toLowerCase() === searchQuery.toLowerCase());
    return !exists;
  }, [onCreate, searchQuery, options]);

  return (
    <div className={`${styles.multiSelectDropdown} ${showLabelOnly ? styles.showLabelOnly : ''} ${className}`} ref={dropdownRef}>
      <button
        ref={triggerRef}
        type="button"
        className={`${styles.trigger} ${showLabelOnly ? `btn btn-small btn-link` : ''}`}
        onClick={() => {
          if (!isOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            switch (direction) {
              case 'up': {
                const available = rect.top - 8;
                setMenuStyle({ bottom: window.innerHeight - rect.top + 4, left: rect.left, minWidth: rect.width });
                setMenuMaxHeight(Math.max(120, Math.min(300, available)));
                break;
              }
              case 'left': {
                const available = rect.bottom - 8;
                setMenuStyle({ bottom: window.innerHeight - rect.bottom, right: window.innerWidth - rect.left + 4 });
                setMenuMaxHeight(Math.max(120, Math.min(300, available)));
                break;
              }
              case 'right': {
                const available = rect.bottom - 8;
                setMenuStyle({ bottom: window.innerHeight - rect.bottom, left: rect.right + 4 });
                setMenuMaxHeight(Math.max(120, Math.min(300, available)));
                break;
              }
              default: {
                const available = window.innerHeight - rect.bottom - 8;
                setMenuStyle({ top: rect.bottom + 4, left: rect.left, minWidth: rect.width });
                setMenuMaxHeight(Math.max(120, Math.min(300, available)));
                break;
              }
            }
          } else {
            setSearchQuery('');
          }
          setIsOpen(!isOpen);
        }}
      >
        {showLabelOnly ? (
          <span className={styles.label}>{placeholder}</span>
        ) : (
          <>
            <span className={styles.label}>
              {selectedValues.length > 0 ? getSelectedLabels() : placeholder}
            </span>
            <span className={`${styles.arrow} ${isOpen ? styles.arrowOpen : ''}`}>
              <ArrowIcon />
            </span>
          </>
        )}
      </button>

      {isOpen && (
        <div className={styles.menu} style={{ ...menuStyle, maxHeight: menuMaxHeight }}>
          {editingValue ? (
            <EditLabel
              initialLabel={editingLabel}
              initialColor={options.find(o => o.value === editingValue)?.color}
              onConfirm={(label, color) => {
                const action = actions?.find(a => a.isEditLabel);
                action?.onClick(editingValue, label, color);
                setEditingValue(null);
              }}
              onCancel={() => setEditingValue(null)}
            />
          ) : (
          <>
          {title && (
            <div className={styles.header}>
              <p className={styles.title}>{title}</p>
              {selectedValues.length > 0 && onChange && (
                <button
                  type="button"
                  className="btn btn-small btn-link"
                  onClick={handleClearAll}
                  aria-label="Clear all selections"
                >
                  Clear all
                </button>
              )}
            </div>
          )}
          {enableSearch && (
            <div className={styles.searchContainer}>
              <input
                type="text"
                placeholder={onCreate ? 'Search or create...' : 'Search...'}
                name="search"
                className={styles.search}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
          )}
          <div className={styles.options}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <div key={option.value} className={`${styles.optionRow} ${actions && actions.length > 0 ? styles.optionRowWithActions : ''} ${confirmDeleteValue === option.value ? styles.confirmingDelete : ''}`}>
                  {confirmDeleteValue === option.value ? (
                    <div className={styles.confirmDelete}>
                      <span className={styles.confirmDeleteText}>
                        <span>Delete '</span>
                        <span className={styles.confirmDeleteLabel}>{option.label}</span>
                        <span>'?</span>
                      </span>
                      <button
                        type="button"
                        className={`${styles.confirmBtn} ${styles.confirmYes}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          const action = actions?.find(a => a.isConfirmDelete);
                          action?.onClick(option.value);
                          setConfirmDeleteValue(null);
                        }}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        className={styles.confirmBtn}
                        onClick={(e) => { e.stopPropagation(); setConfirmDeleteValue(null); }}
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <>
                      <label className={styles.option}>
                          { onChange && (
                            <input
                              type="checkbox"
                              value={option.value}
                              checked={selectedValues.includes(option.value)}
                              onChange={() => handleToggleOption(option.value)}
                            />
                          )}
                          { isLabelOptions ? (
                            <span className={styles.optionLabel}>
                              <ZprLabel
                                key={option.value}
                                text={option.label}
                                size='small'
                                color={option.color}
                              />
                            </span>
                            ) : (
                            <span className={styles.optionLabel}>{option.label}</span>
                          )}
                      </label>
                      {actions && actions.length > 0 && (
                        <div className={styles.optionActions}>
                          {actions.map((action, i) => (
                            <button
                              key={i}
                              type="button"
                              className={styles.optionActionBtn}
                              aria-label={action.label}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (action.isEditLabel) {
                                  setEditingValue(option.value);
                                  setEditingLabel(option.label);
                                } else if (action.isConfirmDelete) {
                                  setConfirmDeleteValue(option.value);
                                } else {
                                  action.onClick(option.value);
                                }
                              }}
                            >
                              {action.icon}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))
            ) : (
              <div className={styles.noResults}>
                No options found
              </div>
            )}
          </div>
          {onCreate && canCreateNewLabel && (
            <div className={styles.createFooter}>
              <button
                type="button"
                className={`${styles.createBtn}`}
                onClick={() => {
                  onCreate(searchQuery);
                  setSearchQuery('');
                }}
              >
                <PlusIcon width="var(--zpr-size-icon-default)" height="var(--zpr-size-icon-default)" />
                <p>Create '{searchQuery}'</p>
              </button>
            </div>
          )}
          </>
          )}
        </div>
      )}
    </div>
  );
};
